/**
 * Блок «Бюджет района» (US-11 FR-6).
 *
 * Состав строк — тот же, что на областной странице (FR-6.4, решение 3 от
 * 03.09): «Всего», «Собственные доходы», «Поступление трансфертов и
 * кредитов из РБ» с вложенными «трансферты», «субвенции», «кредиты».
 * Колонки — «Наименование · План на год · Факт на отчётную дату · Доля, %»;
 * графическая часть — структура бюджета долями (пончик с легендой).
 * Отдельного районного набора строк не вводится.
 *
 * Суммы печатаются с одним знаком в **единице выдачи** (`unitLabel`,
 * US-12 FR-3.1): источник ведёт казначейскую форму в тыс. ₸, и деление
 * на миллион ради «удобных» миллиардов — ровно та ошибка в 1000 раз,
 * которую на экране не видно. Доли исполнения — в процентах с одним
 * знаком (FR-6.3).
 *
 * Метрик исполнения **две, и они разведены** (US-12 FR-9.2): «освоение
 * годового плана» и «исполнение плана периода» — разные величины, и
 * колонки с общим именем «доля, %» здесь нет: 98 % вместо 48 % это
 * неверный управленческий вывод. Правило расчёта приходит из ответа и
 * подписывается под таблицей.
 *
 * Отчётная дата блока своя: бюджет приходит месячным срезом казначейской
 * формы и может отличаться от даты соседних блоков — это нормальное
 * состояние, а не рассогласование (FR-6.2).
 *
 * Доли пончика считаются от плана «Всего» — как доли нацсостава в
 * PopulationNationality; строк без плана в структуре нет.
 */
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../../components/ui/chart';
import { cn } from '../../../../lib/cn';

export type BudgetRowKey =
  | 'total'
  | 'ownIncome'
  | 'republicTransfers'
  | 'transfers'
  | 'subventions'
  | 'credits';

export interface BudgetRowValue {
  /** Уточнённый план на год, в единице выдачи. */
  plan: number | null;
  /** Исполнено на отчётную дату, в единице выдачи. */
  fact: number | null;
  /** Освоение годового плана, % — факт к уточнённому плану года. */
  annualPct: number | null;
  /** Исполнение плана периода, % — отдельная метрика (FR-9.2). */
  periodPct: number | null;
}

/** Порядок и вложенность строк — как на областной странице (FR-6.4). */
const ROWS: Array<{ key: BudgetRowKey; nested: boolean }> = [
  { key: 'total', nested: false },
  { key: 'ownIncome', nested: false },
  { key: 'republicTransfers', nested: false },
  { key: 'transfers', nested: true },
  { key: 'subventions', nested: true },
  { key: 'credits', nested: true },
];

/**
 * Строки структуры бюджета (пончик) и их цвета — значения токенов
 * `chart.*` из tailwind.config.js. Литералами, а не классами: recharts
 * красит сегменты через `fill`, как в PopulationNationality.
 */
const STRUCTURE: Array<{ key: BudgetRowKey; color: string }> = [
  { key: 'ownIncome', color: '#4A63E7' }, // chart-indigo
  { key: 'transfers', color: '#F2D9A4' }, // chart-sand
  { key: 'subventions', color: '#A3CE63' }, // chart-green
  { key: 'credits', color: '#6D7FE3' }, // chart-blue
];

interface Props {
  /** Значения по строкам; нет значения → «—» в ячейке. */
  values?: Partial<Record<BudgetRowKey, BudgetRowValue>>;
  /** Единица сумм из выдачи; экран её не пересчитывает (US-12 FR-3.1). */
  unitLabel?: string | null;
  /** Правило расчёта долей исполнения из выдачи (US-12 FR-9.2). */
  calculationRule?: string | null;
  /** Отчётная дата факта («факт на 1 августа 2026 года»), FR-6.2. */
  periodLabel?: string | null;
}

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v);

export const DistrictBudget = ({
  values,
  unitLabel,
  calculationRule,
  periodLabel,
}: Props) => {
  const { t } = useTranslation();

  const rows = ROWS.map(({ key, nested }) => ({
    key,
    nested,
    value: values?.[key] ?? null,
  }));
  const isEmpty = rows.every(
    (row) =>
      row.value === null ||
      (row.value.plan === null &&
        row.value.fact === null &&
        row.value.annualPct === null &&
        row.value.periodPct === null),
  );

  const totalPlan = values?.total?.plan ?? null;
  const structure =
    totalPlan !== null && totalPlan !== 0
      ? STRUCTURE.flatMap(({ key, color }) => {
          const plan = values?.[key]?.plan ?? null;
          return plan === null
            ? []
            : [
                {
                  key,
                  color,
                  label: t(`digitalPassport.districtBudget.rows.${key}`),
                  percent: (plan / totalPlan) * 100,
                },
              ];
        })
      : [];

  const chartConfig = Object.fromEntries(
    structure.map((segment) => [
      segment.key,
      { label: segment.label, color: segment.color },
    ]),
  ) satisfies ChartConfig;

  return (
    <Card className="shadow-card">
      <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
        <CardTitle>
          {t('digitalPassport.districtBudget.title')}
          <span className="ml-2 text-sm font-normal text-text-muted">
            {unitLabel ?? t('digitalPassport.districtBudget.unit')}
          </span>
        </CardTitle>
        {periodLabel != null && (
          <span
            className="text-sm text-text-muted"
            data-testid="district-budget-period"
          >
            {periodLabel}
          </span>
        )}
      </CardHeader>
      <CardContent>
        <section data-testid="block-district-budget">
          {isEmpty ? (
            <p
              data-testid="block-placeholder"
              className="text-sm lowercase text-text-muted"
            >
              {t('common.noData')}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[26rem] text-sm">
                  <thead>
                    <tr className="border-b border-surface-border text-xs text-text-muted">
                      <th className="py-2 text-left font-medium">
                        {t('digitalPassport.districtBudget.colName')}
                      </th>
                      <th className="py-2 text-right font-medium">
                        {t('digitalPassport.districtBudget.colPlan')}
                      </th>
                      <th className="py-2 text-right font-medium">
                        {t('digitalPassport.districtBudget.colFact')}
                      </th>
                      <th className="whitespace-nowrap py-2 text-right font-medium">
                        {t('digitalPassport.districtBudget.colAnnualPct')}
                      </th>
                      <th className="whitespace-nowrap py-2 text-right font-medium">
                        {t('digitalPassport.districtBudget.colPeriodPct')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ key, nested, value }) => (
                      <tr
                        key={key}
                        data-testid={`district-budget-row-${key}`}
                        className="border-b border-surface-border last:border-0"
                      >
                        <td
                          className={cn(
                            'py-2.5 pr-2',
                            nested ? 'pl-5 text-text-muted' : 'font-bold',
                          )}
                        >
                          {t(`digitalPassport.districtBudget.rows.${key}`)}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {value?.plan != null ? fmt1(value.plan) : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {value?.fact != null ? fmt1(value.fact) : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {value?.annualPct != null
                            ? fmt1(value.annualPct)
                            : '—'}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {value?.periodPct != null
                            ? fmt1(value.periodPct)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {calculationRule != null && calculationRule !== '' && (
                <p
                  className="mt-2 text-xs leading-snug text-text-faint"
                  data-testid="district-budget-rule"
                >
                  {calculationRule}
                </p>
              )}

              {structure.length > 0 && (
                <div
                  className="mt-3.5 flex flex-wrap items-center gap-5 border-t border-surface-border pt-3.5"
                  data-testid="district-budget-structure"
                >
                  <ChartContainer
                    config={chartConfig}
                    className="aspect-square h-[150px] w-[150px]"
                  >
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Pie
                        data={structure}
                        dataKey="percent"
                        nameKey="label"
                        innerRadius="62%"
                        outerRadius="100%"
                        strokeWidth={0}
                      >
                        {structure.map((segment) => (
                          <Cell key={segment.key} fill={segment.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <ul className="min-w-0 flex-1 space-y-1.5 text-sm">
                    {structure.map((segment) => (
                      <li
                        key={segment.key}
                        data-testid={`district-budget-legend-${segment.key}`}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="size-2.5 shrink-0 rounded-sm"
                          style={{ background: segment.color }}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {segment.label}
                        </span>
                        <b className="tabular-nums">
                          {fmt1(segment.percent)} %
                        </b>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
};
